<template>
    <Portal to="modal">
        <ModalContainer
            key="conceptQuizFeedbackSurveyModal"
            :is-dark-mode="isDarkMode"
            :show-close-button="true"
            @close="emitClose"
        >
            <div class="concept-quiz-feedback-survey">
                <template v-if="surveyVariant === 'quizQuit'">
                    <div class="concept-quiz-feedback-survey__title-section" v-dark="isDarkMode">
                        <IconTarget
                            class="concept-quiz-feedback-survey__title-icon"
                        />
                        <div class="
                            concept-quiz-feedback-survey__title 
                            concept-quiz-feedback-survey__title--title-only
                        ">
                            Thanks for giving Concept Quiz a try!
                        </div>
                    </div>
                    <div class="concept-quiz-feedback-survey__form">
                        <Radio
                            v-model="quitEarlyWhy"
                            :data="quitEarlyWhyOptions"
                            :is-dark-mode="isDarkMode"
                            label="We noticed you quit early &mdash; mind sharing why?"
                            class="concept-quiz-feedback-survey__field"
                        />
                        <PocketTextarea
                            v-model="anythingElseToShare"
                            label="Anything else you'd like to share?"
                            class="concept-quiz-feedback-survey__field concept-quiz-feedback-survey__field-input"
                            :is-dark-mode="isDarkMode"
                            height="62"
                            width=""
                            @keydown.enter="submitForm"
                        />
                    </div>
                    <div class="concept-quiz-feedback-survey__actions">
                        <PocketButton
                            class="concept-quiz-feedback-survey__dismiss-btn"
                            :is-dark-mode="isDarkMode"
                            @click="emitClose"
                            type="secondary"
                        >
                            Cancel
                        </PocketButton>
                        <PocketButton
                            class="concept-quiz-feedback-survey__submit-btn"
                            :disabled="isQuitSubmitDisabled"
                            :is-dark-mode="isDarkMode"
                            @click="submitForm"
                        >
                            Submit
                        </PocketButton>
                    </div>
                </template>
                <template v-else>
                    <div class="concept-quiz-feedback-survey__title-section" v-dark="isDarkMode">
                        <IconTarget
                            class="concept-quiz-feedback-survey__title-icon"
                        />
                        <div class="concept-quiz-feedback-survey__title">
                            Thanks for trying a few Concept Quizzes!
                        </div>
                        <div class="concept-quiz-feedback-survey__text" v-dark>
                            We would love your feedback.
                        </div>
                    </div>
                    <div class="concept-quiz-feedback-survey__form">
                        <Radio
                            v-model="helpful"
                            :data="helpfulOptions"
                            :is-dark-mode="isDarkMode"
                            class="concept-quiz-feedback-survey__field"
                            label="Did you find concept quizzes helpful?"
                        />
                        <PocketTextarea
                            v-model="whatWasHelpful"
                            label="What was helpful, or what could we improve?"
                            class="concept-quiz-feedback-survey__field concept-quiz-feedback-survey__field-input"
                            :is-dark-mode="isDarkMode"
                            height="62"
                            width=""
                            @keydown.enter="submitForm"
                        />
                        <PocketTextarea
                            v-model="otherConcepts"
                            label="What other concepts would you like to take quizzes on?"
                            class="concept-quiz-feedback-survey__field concept-quiz-feedback-survey__field-input"
                            :is-dark-mode="isDarkMode"
                            height="62"
                            width=""
                            @keydown.enter="submitForm"
                        />
                    </div>
                    <div class="concept-quiz-feedback-survey__actions">
                        <PocketButton
                            class="concept-quiz-feedback-survey__dismiss-btn"
                            :is-dark-mode="isDarkMode"
                            @click="emitClose"
                            type="secondary"
                        >
                            Cancel
                        </PocketButton>
                        <PocketButton
                            class="concept-quiz-feedback-survey__submit-btn"
                            :disabled="isCompletedSubmitDisabled"
                            :is-dark-mode="isDarkMode"
                            @click="submitForm"
                        >
                            Submit
                        </PocketButton>
                    </div>
                </template>
            </div>
        </ModalContainer>
    </Portal>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-facing-decorator'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
import { toastModule } from '@/store/toast/module'
import IconTarget from '@/components/ConceptQuizExperiment/IconTarget.vue'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'

export type ConceptQuizConfig = {
    conceptConfig: {
        concept: string
        examMetadataId: string
        questionSerials: string[]
        questionCountOptions: number[]
    }
    conceptQuizQuestionCount: number
    incorrectCount: number
    quizId: string
}

@Component({
    components: {
        ModalContainer: UIKit.ModalContainer,
        PocketButton: UIKit.Button,
        Modal: UIKit.Modal,
        PocketTextarea: UIKit.Textarea,
        Radio: UIKit.Radio,
        IconTarget,
    },
})
export default class ExplainYourAnswerFeedbackSurvey extends Vue {
    @Prop() surveyVariant?: 'quizQuit' | 'quizCompleted'

    conceptQuizConfig: ConceptQuizConfig | null = null

    // completed survey data
    whatWasHelpful = ''
    otherConcepts = ''
    helpfulOptions = [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unsure', label: 'I\'m not sure' },
        { value: 'didntknow', label: 'I didn\'t know I took a concept quiz' },
    ]
    helpful: typeof this['helpfulOptions'][number] | null = null

    // quit early survey
    quitEarlyWhyOptions = [
        { value: 'idonthavetime', label: 'I don\'t have time right now' },
        { value: 'idontlikequiz', label: 'I don\'t like the quiz' },
        { value: 'ididntmeanto', label: 'I didn\'t mean to quit' },
        { value: 'other', label: 'Other' },
    ]
    quitEarlyWhy: typeof this['quitEarlyWhyOptions'][number] | null = null
    anythingElseToShare = ''

    get isDarkMode () {
        return userModule.state.settings.isDarkMode
    }

    get isCompletedSubmitDisabled () {
        return !this.helpful
    }

    get isQuitSubmitDisabled () {
        return !this.quitEarlyWhy
    }

    get triggeringQuiz () {
        return this.conceptQuizConfig
            ? quizModule.getters.getQuizById(this.conceptQuizConfig.quizId)
            : undefined
    }

    get examMetadata () {
        return examMetadataModule.getters.getCurrentExamMetadata()
    }

    mounted () {
        const rawConceptQuizConfig = localStorage.getItem('conceptQuizExperiment_conceptQuizConfig') || '{}'
        this.conceptQuizConfig = JSON.parse(rawConceptQuizConfig)

        if (!this.conceptQuizConfig) {
            this.emitClose()
        }
    }

    submitForm () {
        const conceptQuizConceptQuizzesTaken =
                JSON.stringify(userModule.state.user?.webConfig?.conceptQuizConceptsTaken || [])

        if (this.surveyVariant === 'quizQuit') {
            analyticsModule.actions.amplitudeTrack('Concept Quiz Survey Answered', {
                conceptQuizSurveyQuitEarlyWhy: this.quitEarlyWhy?.label,
                conceptQuizSurveyAnythingElseToShare: this.anythingElseToShare,
                examGuid: this.examMetadata?.examGuid || '',
                conceptQuizConcept: this.conceptQuizConfig?.conceptConfig.concept,
                conceptQuizTriggerCount: this.conceptQuizConfig?.incorrectCount,
                conceptQuizQuestionCount: this.conceptQuizConfig?.conceptQuizQuestionCount,
                conceptQuizSurveyVariant: this.surveyVariant,
                conceptQuizConceptQuizzesTaken,
            })
            localStorage.setItem('conceptQuizExperiment_hasAnsweredQuitSurvey', 'true')
        } else {
            analyticsModule.actions.amplitudeTrack('Concept Quiz Survey Answered', {
                conceptQuizSurveyHelpful: this.helpful?.label,
                conceptQuizSurveyWhatWasHelpful: this.whatWasHelpful,
                conceptQuizSurveyOtherConcepts: this.otherConcepts,
                examGuid: this.examMetadata?.examGuid || '',
                conceptQuizConcept: this.conceptQuizConfig?.conceptConfig.concept,
                conceptQuizTriggerCount: this.conceptQuizConfig?.incorrectCount,
                conceptQuizQuestionCount: this.conceptQuizConfig?.conceptQuizQuestionCount,
                conceptQuizSurveyVariant: this.surveyVariant,
                conceptQuizConceptQuizzesTaken,
            })
            localStorage.setItem('conceptQuizExperiment_hasAnsweredCompletedSurvey', 'true')
        }
        toastModule.actions.displayToast({
            title: 'Thank you for your feedback!',
        })
        this.emitClose()
    }

    @Emit('close')
    emitClose () {
        return true
    }
}
</script>

<style lang="scss" scoped>
.concept-quiz-feedback-survey {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 520px;
    box-sizing: border-box;
    
    @include breakpoint(black-bear) {
        width: 100%;
        margin: 0 auto;
    }

    &__title-section {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 24px 32px 0;
        box-sizing: border-box;
        border-bottom: 1px solid $fog;

        @include breakpoint(black-bear) {
            padding: 24px 32px 0 32px;
        }

        &--dark {
            border-color: $ash;
        }
    }

    &__title-icon {
        width: 18px;
        height: 18px;
        margin-bottom: 8px;
        color: $ash;
    }

    &__title {
        font-weight: 600;
        font-size: 18px;
        line-height: 25px;
        margin-bottom: 6px;
        letter-spacing: 0.3px;

        &--title-only {
            margin-bottom: 20px;
        }

        @include breakpoint(black-bear) {
            width: 110%;
            text-align: center;
        }
    }

    &__text {
        font-size: 15px;
        line-height: 20px;
        color: $ash;
        margin-bottom: 30px;

        &--dark {
            color: $fog;
        }
    }

    &__form {
        margin-top: 30px;
        padding: 0 40px;
        box-sizing: border-box;
        width: 100%;
    }

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 94px;
    }

    &__field {
        margin-bottom: 20px;
    }

    &__field :deep(.uikit-radio__label),
    &__field :deep(.uikit-textarea__label) {
        color: $brand-black;
        margin-left: 0;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;

        @include breakpoint(black-bear) {
            padding-right: 0;
        }

        &.uikit-radio__label--dark,
        &.uikit-textarea__label--dark {
            color: $white;
        }
    }

    &__field :deep(.uikit-radio__label) {
        margin-bottom: 10px;
    }

    &__field :deep(.uikit-radio__item--dark::before) {
        background-color: $pickled-bluewood;
    }

    &__field-input :deep(.uikit-textarea__textarea) {
        margin-left: -8px;
        margin-right: -8px;
        width: calc(100% + 16px);
        height: 62px;
        display: block;

        @include breakpoint(black-bear) {
            height: 72px;
        }
    }

    &__actions {
        margin-top: 10px;
        align-self: center;
        padding: 0 40px 32px;
    }

    &__submit-btn {
        margin-left: 8px;
    }
}
</style>